import React, { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/** A box in viewport coordinates (a DOMRect fits). */
export interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface SpotlightProps {
  /** Element (measured and followed on scroll and resize) or viewport rect to leave uncovered; null dims everything. */
  target: HTMLElement | SpotlightRect | null;
  /** Space between the target and the edge of the cut-out, in px. Default 8. */
  padding?: number;
  /** Called when the dimmed area is clicked. The page underneath never receives the click. */
  onBackdropClick?: () => void;
  /** Floating content shown above the dim (e.g. a tour tooltip). Not hidden from screen readers. */
  children?: React.ReactNode;
}

/** The cut-out for `rect` grown by `padding` (clamped at the top and left edges of the viewport). */
export function spotlightRect(rect: SpotlightRect, padding: number): SpotlightRect & { right: number; bottom: number } {
  return {
    top: Math.max(rect.top - padding, 0),
    left: Math.max(rect.left - padding, 0),
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    right: rect.left + rect.width + padding,
    bottom: rect.top + rect.height + padding,
  };
}

function isElement(target: SpotlightProps['target']): target is HTMLElement {
  return typeof HTMLElement !== 'undefined' && target instanceof HTMLElement;
}

/** Stop the page under the spotlight from receiving pointer, wheel and touch input. */
function swallow(e: React.SyntheticEvent) {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * Full-screen dim with a rounded cut-out around one element, on the popover layer
 * (above dialogs, below toasts and blocking prompts). It blocks the page but is not a
 * dialog: it takes no part in the dialog stack, focus trap or scroll lock, and the dim
 * is aria-hidden. Put the explanation (a tooltip) in `children`.
 */
export default function Spotlight({ target, padding = 8, onBackdropClick, children }: SpotlightProps) {
  const [measured, setMeasured] = useState<SpotlightRect | null>(null);
  const element = isElement(target) ? target : null;

  useLayoutEffect(() => {
    if (!element) {
      setMeasured(null);
      return undefined;
    }
    let raf = 0;
    const measure = () => setMeasured(element.getBoundingClientRect());
    const schedule = () => {
      if (raf) window.cancelAnimationFrame(raf);
      raf = window.requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    return () => {
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [element]);

  const rect = element ? measured : (target as SpotlightRect | null);
  const hole = rect ? spotlightRect(rect, padding) : null;

  return createPortal(
    <div className="fixed inset-0 z-popover overflow-hidden overscroll-none" data-spotlight="">
      <div
        aria-hidden
        className="absolute inset-0"
        onPointerDown={swallow}
        onClick={(e) => {
          swallow(e);
          onBackdropClick?.();
        }}
        onWheel={swallow}
        onTouchMove={swallow}
      >
        {hole ? (
          <div
            data-spotlight-hole=""
            className="pointer-events-none absolute rounded-container border-2 border-focus shadow-[0_0_0_9999px_var(--k-scrim)]"
            style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
          />
        ) : (
          <div className="pointer-events-none absolute inset-0 bg-scrim" />
        )}
      </div>
      {children}
    </div>,
    document.body
  );
}
